import { Component, Input, OnInit } from '@angular/core';
import { AppComponentName, ExportData, ExportDataCriteria, ExportSaveAs, InterpolationInterval, InterpolationMethod, Message, MessageActionType, MessageType, Sensor, TimeZoneDisplay } from '@models';
import { AppMessageService } from '@services/message.service';
import { SensorService } from '@services/sensor.service';
import { UserService } from '@services/user.service';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

@Component({
  standalone: false,
  selector: 'export-data-modal',
  templateUrl: './export-data-modal.component.html',
  styleUrls: ['./export-data-modal.component.scss']
})

export class ExportDataModalComponent implements OnInit {

  @Input() sensor: Sensor;
  @Input() selectedDateRange: DateRange;
  @Input() selectedTimeZone: TimeZoneDisplay;
  @Input() selectedTimeZoneOffset: number;
  @Input() includeOrigValues: boolean = false;
  @Input() includeCalValues: boolean = true;

  modalDragOn: boolean = false;

  exportOptionsModalRef: BsModalRef;
  username = 'unknown';

  exportDataCriteria: ExportDataCriteria = {
    filename: 'exportData',
    saveAs: ExportSaveAs.CSV2,
    startDate: moment().startOf('day').format('YYYY-MM-DD HH:mm'),
    endDate: moment().endOf('day').format('YYYY-MM-DD HH:mm'),
    sensorReadingTypes: [],
    interpolateData: false,
    interpolateInterval: InterpolationInterval.I30,
    interpolateIntervalValue: 30,
    interpolateMethod: InterpolationMethod.Linear,
    ignoreOutliers: false,
    ignoreOutliersLessThan: undefined,
    ignoreOutliersGreaterThan: undefined,
    uuids: [ ],
    retrieveRawValues: false,
    includeGpsInfo: false,
    timeZoneDisplay: TimeZoneDisplay.Local,
    timeZoneOffset: moment().utcOffset(),
    includeOrigValues: this.includeOrigValues,
    includeCalValues: this.includeCalValues
  };


  constructor(public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService,
              public _sensorService: SensorService, public _userService: UserService, public _appMessageService: AppMessageService) {
  }


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    var readingTypes: string[] = this.sensor.sensorReadingTypes.filter(x => x.checked).map(y => y.type);

    this.exportDataCriteria = {
      filename: 'exportData',
      saveAs: ExportSaveAs.CSV2,
      startDate: this.selectedDateRange.startDate.format('YYYY-MM-DD HH:mm'),
      endDate: this.selectedDateRange.endDate.format('YYYY-MM-DD HH:mm'),
      sensorReadingTypes: readingTypes,
      interpolateData: false,
      interpolateInterval: InterpolationInterval.I30,
      interpolateIntervalValue: 30,
      interpolateMethod: InterpolationMethod.Linear,
      ignoreOutliers: false,
      ignoreOutliersLessThan: undefined,
      ignoreOutliersGreaterThan: undefined,
      uuids: [ this.sensor.uuid ],
      retrieveRawValues: false,
      includeGpsInfo: false,
      timeZoneDisplay: this.selectedTimeZone,
      timeZoneOffset: this.selectedTimeZoneOffset,
      includeOrigValues: this.includeOrigValues,
      includeCalValues: this.includeCalValues
    };
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  ngOnChanges() {
    this.exportDataCriteria.sensorReadingTypes = this.sensor.sensorReadingTypes.filter(x => x.checked).map(y => y.type);
    this.exportDataCriteria.startDate = this.selectedDateRange.startDate.format('YYYY-MM-DD HH:mm');
    this.exportDataCriteria.endDate = this.selectedDateRange.endDate.format('YYYY-MM-DD HH:mm');
    this.exportDataCriteria.timeZoneDisplay = this.selectedTimeZone;
    this.exportDataCriteria.timeZoneOffset = this.selectedTimeZoneOffset;
  }


  exportDataWithOptions() {
    if (this.exportDataCriteria.filename == '') {
      this.toastr.warning('Please provide a filename for the exported data');
      return;
    }

    this.exportDataCriteria.includeCalValues = this.includeCalValues;
    this.exportDataCriteria.includeOrigValues = this.includeOrigValues;
    this.exportDataCriteria.retrieveRawValues = this.includeOrigValues;

    switch (this.exportDataCriteria.interpolateInterval) {
      case InterpolationInterval.I15:
        this.exportDataCriteria.interpolateIntervalValue = 15;
        break;
      case InterpolationInterval.I30:
        this.exportDataCriteria.interpolateIntervalValue = 30;
        break;
      case InterpolationInterval.I60:
        this.exportDataCriteria.interpolateIntervalValue = 60;
        break;
      case InterpolationInterval.I1440:
        this.exportDataCriteria.interpolateIntervalValue = 1440;
        break;
      default:
        this.exportDataCriteria.interpolateIntervalValue = 15;
        break;
    }

    if (this.exportDataCriteria.interpolateData == false) {
      this.exportDataCriteria.interpolateIntervalValue = undefined;
    }

    this.exportDataCriteria.timeZoneDisplay = this.selectedTimeZone;
    this.exportDataCriteria.timeZoneOffset = this.selectedTimeZoneOffset;

    this._sensorService.exportSensorReadingsAsText(this.exportDataCriteria).subscribe(
      results => {
        if (results != null) {
          if (results.length == 0) {
            this.toastr.warning('No data associated with the given sensors and types');
          }
          else {
            this.saveExportToFile(results);
          }
        } else {
          this.toastr.warning('No sensor readings found to export');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while attempting to export sensor data');
      });
  }


  saveExportToFile(fileContent: string) {
    if (this.exportDataCriteria.saveAs == ExportSaveAs.JSON) {
      this.saveData(fileContent, this.exportDataCriteria.filename + '.json');
    }
    else if (this.exportDataCriteria.saveAs == ExportSaveAs.CSV) {
      this.saveData(fileContent, this.exportDataCriteria.filename + '.csv');
    }
    else if (this.exportDataCriteria.saveAs == ExportSaveAs.CSV2) {
      this.saveData(fileContent, this.exportDataCriteria.filename + '.csv');
    }

    var msg = `Exported readings to ${this.exportDataCriteria.saveAs} file`;
    if (this.exportDataCriteria.interpolateData) msg += ' with interpolation';
    if (this.exportDataCriteria.includeGpsInfo) msg += ' with GPS info';
    this.createMessageData(MessageActionType.New, this.sensor, "Exported Readings: " + moment(this.exportDataCriteria.startDate).format("YYYY-MM-DD hh:mm a").toString() + " to " + moment(this.exportDataCriteria.endDate).format("YYYY-MM-DD hh:mm a").toString());
  }


  setInterpolateData(state: boolean) {
    this.exportDataCriteria.interpolateData = state;
  }


  saveData(dataString: string, fileName: string) {
    const blob = new Blob(['\uFEFF', dataString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
  }


  createMessageData(msgActionType: MessageActionType, sensor: Sensor, msgNote: string) {
    const sensorShortName = (sensor.uuid.length < 10) ? sensor.uuid : sensor.uuid.substring(0,3) + '...' + sensor.uuid.substring(sensor.uuid.length-6);
    const displayName = (sensor.name != undefined && sensor.name !== '') ? sensor.name : sensorShortName;

    const message: Message = {
      id: 0,
      messageType: MessageType.Audit,
      appComponentName: AppComponentName.Sensors,
      actionType: msgActionType,
      appComponentItem: displayName,
      details: msgNote,
      userNotes: '',
      count: 1,
      acknowledged: false,
      userName: this.username,
      userGroupId: null
    }

    this._appMessageService.createAppMessage(message).subscribe(
      results => {
        if (results != null) {
          //this.toastr.success('Success');
        } else {
          //this.toastr.warning('Not found.');
        }
        return;
      },
      err => {
        //this.toastr.error('Error occurred while saving change log.');
      });
  }


}
